if (!customElements.get('quick-add-modal')) {
  // Cache for storing fetched product content
  const productCache = new Map();
  const prefetchInProgress = new Set();

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        // Enable hover prefetching for better perceived performance
        this.setupHoverPrefetch();
      }

      setupHoverPrefetch() {
        // Prefetch product content on hover (aggressive but effective)
        document.addEventListener('mouseover', (event) => {
          const button = event.target.closest('[data-product-url]');
          if (!button) return;

          const productUrl = button.getAttribute('data-product-url');
          if (!productUrl || productCache.has(productUrl) || prefetchInProgress.has(productUrl)) return;

          // Mark as in progress to avoid duplicate requests
          prefetchInProgress.add(productUrl);

          // Prefetch after a small delay to avoid prefetching on accidental hovers
          setTimeout(() => {
            if (productCache.has(productUrl)) {
              prefetchInProgress.delete(productUrl);
              return;
            }

            fetch(productUrl)
              .then((response) => response.text())
              .then((responseText) => {
                const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
                const productElement = responseHTML.querySelector('product-info');

                if (productElement) {
                  productCache.set(productUrl, productElement.cloneNode(true));
                }
              })
              .catch(() => {
                // Silently fail - will fetch on click if prefetch fails
              })
              .finally(() => {
                prefetchInProgress.delete(productUrl);
              });
          }, 100); // 100ms delay before prefetching
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;

        // Save current slider position before hiding
        const swiperParent = this.openedBy?.closest('.swiper');
        const swiperInstance = swiperParent?.swiper;
        const currentIndex = swiperInstance?.activeIndex;

        super.hide();

        // Restore slider position after hide
        if (swiperInstance && typeof currentIndex === 'number') {
          setTimeout(() => {
            swiperInstance.slideTo(currentIndex, 0); // 0 = no animation
          }, 0);
        }
      }

      show(opener) {
        const productUrl = opener.getAttribute('data-product-url');

        // Save slider position before showing modal
        const swiperParent = opener.closest('.swiper');
        const swiperInstance = swiperParent?.swiper;
        this.savedSlideIndex = swiperInstance?.activeIndex;

        // Show modal immediately with loading state
        this.showLoadingState();
        super.show(opener);

        // Check cache first
        if (productCache.has(productUrl)) {
          // IMPORTANT: Clone the cached element to avoid mutating the cache
          // preprocessHTML modifies the element (IDs, etc.), so we need a fresh copy
          this.renderProduct(productCache.get(productUrl).cloneNode(true), opener);
          return;
        }

        // Fetch and cache product content
        this.fetchProduct(productUrl, opener);
      }

      showLoadingState() {
        this.modalContent.innerHTML = `
          <div class="quick-add-loading" style="display: flex; align-items: center; justify-content: center; min-height: 400px;">
            <div class="loading__spinner">
              <svg focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <title>Loading</title>
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </div>
        `;
      }

      fetchProduct(productUrl, opener) {
        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            // Cache the product element
            productCache.set(productUrl, productElement.cloneNode(true));

            this.renderProduct(productElement, opener);
          })
          .catch((error) => {
            console.error('Error loading product:', error);
            this.modalContent.innerHTML = '<div style="padding: 2rem; text-align: center;">Error loading product. Please try again.</div>';
          });
      }

      renderProduct(productElement, opener) {
        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        // Initialize Yotpo reviews after HTML is loaded into modal
        this.initializeYotpo();
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeYotpo() {
        // Simple approach: check for yotpoWidgetsContainer and initialize
        setTimeout(() => {
          if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
            window.yotpoWidgetsContainer.initWidgets(false);
          }
        }, 100);
      }
    }
  );
}
